import { z } from 'zod';

// WITTCO Remote contracts shared by the agent routes, the console and its viewer.

/** What the agent reports about MeshAgent on its device. The node ID is MeshAgent's own, from its registry key. */
export const remoteReportSchema = z.object({
  state: z.enum(['absent', 'installing', 'running', 'stopped', 'failed']),
  nodeId: z.string().regex(/^[A-Za-z0-9@$]{64}$/).optional(),
  version: z.string().trim().max(40).optional(),
  detail: z.string().trim().max(500).optional(),
}).strict();
export type RemoteReport = z.infer<typeof remoteReportSchema>;

/** The answer: what the device should have. `remote: null` means this server has no WITTCO Remote; leave MeshAgent alone. */
export interface RemoteInstruction {
  remote: null | {
    want: 'installed';
    /** MeshAgent's Windows service name, and the settings (.msh) and tag files to install beside it. */
    serviceName: string; settings: string; tag: string;
    /** Whether the server matched the reported node to this device; until then the device keeps reporting. */
    verified: boolean;
    /**
     * The MeshAgent to install: a release manifest (product wittco-remote) the agent checks against its compiled-in
     * release keys, and whose package it downloads from /agent/v1/remote/package/:version. Null until one is published.
     */
    package: { version: string; manifest: string } | null;
  };
}

export type RemoteState = 'not_configured' | 'unreachable' | 'not_installed' | 'installing' | 'failed' | 'stopped' | 'offline' | 'ready';
/**
 * How a session ended. `device`: the device ended it, MeshAgent still online (the person at the computer, say); `lost`:
 * the device went offline mid-session (it restarted, or lost its network), which a reconnect may continue (WRMM-66).
 */
export type SessionEndReason = 'closed' | 'device' | 'revoked' | 'expired' | 'unavailable' | 'failed' | 'interrupted' | 'removed'
  | 'declined' | 'unanswered' | 'lost';

/**
 * A customer's consent setting (WRMM-64): ask the person at the computer before a technician connects, or not; how long
 * they have to answer; and whether no answer (or nobody there to answer) refuses the session instead of letting the
 * technician in. Owners change it; technicians can't.
 */
export const remoteConsentSchema = z.object({
  ask: z.boolean(), seconds: z.union([z.literal(15), z.literal(30), z.literal(60)]), denyUnanswered: z.boolean(),
}).strict();
export type RemoteConsent = z.infer<typeof remoteConsentSchema>;
export interface RemoteConsentView extends RemoteConsent { organizationId: string; canChange: boolean }
/**
 * What happened with a session's consent: not asked; allowed or declined by the person at the computer; nobody answered
 * in time, then let in or refused; or nobody was there to answer (no one signed in, or the screen locked), then let in or
 * refused when the time was up.
 */
export type ConsentOutcome = 'not_asked' | 'allowed' | 'declined' | 'timeout_allowed' | 'timeout_refused' | 'unattended_allowed' | 'unattended_refused';

export interface RemoteSessionView {
  /** `active`: a relay has it now, connecting or connected; `connectedAt` is set once it was let in (WRMM-64). */
  id: string; deviceId: string; kind: RemoteSessionKind; terminalMode?: TerminalMode; mode: 'control' | 'view'; personId: string; mine: boolean; active: boolean;
  requestedAt: string; connectedAt: string | null; endedAt: string | null; endReason: SessionEndReason | null;
  durationSeconds: number | null; bytesToDevice: number; bytesFromDevice: number;
  consent: RemoteConsent; consentOutcome: ConsentOutcome | null;
  /** A reconnect: the session it continues (WRMM-66). */
  continues: string | null;
}
export interface RemoteStatus {
  state: RemoteState;
  /** Why the state isn't ready, in words for the technician (an install error the device reported, for instance). */
  detail: string | null;
  /** Whether the caller holds remote.connect for this device. */
  canConnect: boolean;
  desktopAvailable?: boolean; desktopDetail?: string | null;
  desktopConsent?: 'portal';
  agentVersion: string | null; reportedAt: string | null;
  sessions: RemoteSessionView[];
}

/** Connection facts only (WRMM-404): no session ticket, owner capabilities or session settlement. */
export type RemoteConnectionStatus = Pick<RemoteStatus, 'state' | 'detail' | 'desktopAvailable' | 'desktopDetail' | 'desktopConsent'
  | 'agentVersion' | 'reportedAt'> & { checkedAt: string };

/**
 * `continues`: reconnect after the device came back (WRMM-66). Allowed for the person's own session on this device, in
 * the same mode, that connected and then dropped as the device went offline (or the API restarted) less than
 * RECONNECT_SECONDS ago; a control session's key touch covers its reconnects, so the ticket needs no approval.
 * `terminal` (WRMM-68): a full-access PowerShell terminal, always a control session (key-approved), never a reconnect.
 */
export const terminalModes = ['shell', 'support', 'tmux', 'tmux-view'] as const;
export type TerminalMode = typeof terminalModes[number];
export const remoteSessionRequestSchema = z.object({
  terminalMode: z.enum(terminalModes).default('shell'),
  mode: z.enum(['control', 'view']).default('control'), continues: z.uuid().optional(), kind: z.enum(['desktop', 'terminal']).default('desktop'),
}).strict().refine(r => r.kind === 'terminal' || r.terminalMode === 'shell', 'tmux requires a terminal').refine(r => r.kind === 'desktop' || (r.mode === 'control' && !r.continues), 'A terminal is a control session, and isn’t reconnected');
export type RemoteSessionKind = 'desktop' | 'terminal';
/** Restarting the device from a control session (WRMM-66): covered by the session's own key touch. */
export const remoteRestartSchema = z.object({ safeMode: z.boolean().default(false) }).strict();
/**
 * A requested session. A control session also carries WebAuthn options: the technician's key approves that exact
 * session (POST /v1/remote-sessions/:id/approve) before its viewer can connect. View-only sessions need no approval.
 */
export interface RemoteSessionTicket { id: string; kind: RemoteSessionKind; mode: 'control' | 'view'; approval: Record<string, unknown> | null; consent: RemoteConsent }

/** Messages the API's relay sends the viewer as JSON text frames; the device's screen arrives as binary frames. */
export type ViewerMessage =
  | { type: 'desktop'; backend: 'wayland'; unattended?: true }
  /** `dropped`: the device's side closed without saying why; the relay is finding out whether it went offline. */
  | { type: 'state'; state: 'connecting' | 'connected' | 'dropped' }
  | { type: 'notice'; message: string }
  /** The display switch the viewer asked for wasn't passed on (WRMM-67): another session shares the device's screen. */
  | { type: 'display-refused'; message: string }
  /** The person at the computer is being asked (or, with nobody there to answer, the timeout is running out), then the outcome. */
  | { type: 'consent'; state: 'waiting'; seconds: number; unattended: boolean; letInAfter: boolean }
  | { type: 'consent'; state: ConsentOutcome }
  /** `byPerson`: the person at the computer ended it with WITTCO's tab (WRMM-176), so it is never reconnected or retried. */
  | { type: 'ended'; reason: SessionEndReason; message: string; byPerson?: true }
  /** How a clipboard transfer the viewer asked for went (WRMM-63); fetching the device's clipboard carries its text. */
  | { type: 'clipboard'; id: string; direction: ClipboardDirection; ok: true; bytes: number; text?: string }
  | { type: 'clipboard'; id: string; direction: ClipboardDirection; ok: false; error: string };
/** The viewer's own text messages; its binary frames are desktop commands (desktop-protocol.ts). */
/** From the viewer: end the session; `failed` when it ends because the device sent something it can't show. */
export type ViewerCommand = { type: 'end'; reason?: 'failed' } | ClipboardCommand;
/**
 * Copy and paste (WRMM-63), in a control session: put text on the device's clipboard, or fetch what is on it. `id` is
 * the viewer's own, echoed in the answer.
 */
export type ClipboardCommand = { type: 'clipboard'; id: string; direction: 'to_device'; text: string } | { type: 'clipboard'; id: string; direction: 'from_device' };
export type ClipboardDirection = 'to_device' | 'from_device';
/** The most text one clipboard transfer carries, either way, in UTF-8 bytes. */
export const CLIPBOARD_MAX_BYTES = 64 * 1024;
/**
 * A terminal's own text messages (WRMM-68): end it, or its new size. What the technician types goes as binary frames
 * (UTF-8), and what the device shows comes back as binary frames; the relay's messages are ViewerMessage text frames.
 */
export type TerminalCommand = { type: 'end' } | { type: 'resize'; cols: number; rows: number };
/** A terminal's size bounds, and the most transcript kept per session (beyond it the transcript says it was cut short). */
export const TERMINAL_COLS = { min: 20, max: 400 }, TERMINAL_ROWS = { min: 5, max: 200 };
export const TRANSCRIPT_MAX_BYTES = 32 * 1024 * 1024;
/**
 * Shown in the terminal before anything typed is sent, and kept as the transcript's first part (WRMM-172): what the
 * device shows comes after it.
 */
export const TRANSCRIPT_NOTICE = '\r\n\u001b[1;33mEverything typed in this terminal is recorded. Don\'t type passwords.\u001b[0m\r\n\r\n';
/**
 * A terminal session's transcript, in order: what the device showed (out), the terminal's size whenever it was set
 * (size, "<cols>;<rows>": WRMM-171) and, for MSP-wide owners, what was typed (in), as UTF-8 text (WRMM-172). `typed` says whether what was typed is included: `owners_only` when the reader isn't an
 * MSP-wide owner, `not_kept` for sessions from before typed keys were sealed (their typed keys were deleted), and
 * `unavailable` when it couldn't be unsealed. `expired`: the session is older than the 30 days transcripts are kept.
 */
export interface TerminalTranscript {
  sessionId: string; entries: Array<{ at: string; direction: 'in' | 'out' | 'size'; text: string }>;
  typed: 'included' | 'owners_only' | 'not_kept' | 'unavailable'; expired: boolean;
}

/** How long a control session may wait for its key approval, then for its viewer to connect; the longest a session may last. */
export const SESSION_APPROVE_SECONDS = 180;
export const SESSION_CONNECT_SECONDS = 60;
/** A relay touches its session this often; one not touched for RELAY_STALE_SECONDS has no live relay anywhere. */
export const RELAY_SEEN_SECONDS = 15;
export const RELAY_STALE_SECONDS = 60;
export const SESSION_MAX_SECONDS = 8 * 60 * 60;
/**
 * How long after a session dropped (its device went offline) the same technician may reconnect to it without another
 * key touch (Harry, 2026-10-09: "within 10 minutes"); a control session's key touch covers its reconnects for up to
 * SESSION_MAX_SECONDS from the touch.
 */
export const RECONNECT_SECONDS = 10 * 60;
